package org.zq.linkgame.ui.navigation

import androidx.compose.animation.*
import androidx.compose.animation.core.tween
import androidx.compose.runtime.*
import org.zq.linkgame.data.SettingsStorage
import org.zq.linkgame.ui.model.AppLanguage
import org.zq.linkgame.ui.model.BackgroundTheme
import org.zq.linkgame.ui.model.ProvideStrings
import org.zq.linkgame.ui.model.LevelPack
import org.zq.linkgame.ui.model.Screen
import org.zq.linkgame.ui.screens.GameScreen
import org.zq.linkgame.ui.screens.LeaderboardScreen
import org.zq.linkgame.ui.screens.LevelSelectScreen
import org.zq.linkgame.ui.screens.MainScreen

/**
 * Navigation state for the app
 */
sealed class NavigationState {
    data object Main : NavigationState()
    data object LevelSelect : NavigationState()
    data object Leaderboard : NavigationState()
    data class Game(val levelId: Int) : NavigationState()
}

/**
 * Main navigation composable that handles screen transitions
 */
@Composable
fun LinkGameNavigation() {
    val settingsStorage = remember { SettingsStorage() }
    var navigationState by remember { mutableStateOf<NavigationState>(NavigationState.Main) }
    var backgroundTheme by remember { mutableStateOf(settingsStorage.getBackgroundTheme()) }
    var currentLanguage by remember { mutableStateOf(settingsStorage.getLanguage()) }
    
    ProvideStrings(language = currentLanguage) {
        AnimatedContent(
            targetState = navigationState,
            transitionSpec = {
            when {
                targetState is NavigationState.Main -> {
                    slideInHorizontally(
                        animationSpec = tween(300),
                        initialOffsetX = { -it }
                    ) togetherWith slideOutHorizontally(
                        animationSpec = tween(300),
                        targetOffsetX = { it }
                    )
                }
                initialState is NavigationState.Main && targetState is NavigationState.LevelSelect -> {
                    slideInHorizontally(
                        animationSpec = tween(300),
                        initialOffsetX = { it }
                    ) togetherWith slideOutHorizontally(
                        animationSpec = tween(300),
                        targetOffsetX = { -it }
                    )
                }
                initialState is NavigationState.LevelSelect && targetState is NavigationState.Game -> {
                    slideInHorizontally(
                        animationSpec = tween(300),
                        initialOffsetX = { it }
                    ) togetherWith slideOutHorizontally(
                        animationSpec = tween(300),
                        targetOffsetX = { -it }
                    )
                }
                else -> {
                    slideInHorizontally(
                        animationSpec = tween(300),
                        initialOffsetX = { -it }
                    ) togetherWith slideOutHorizontally(
                        animationSpec = tween(300),
                        targetOffsetX = { it }
                    )
                }
            }
        }
    ) { state ->
        when (state) {
            is NavigationState.Main -> {
                MainScreen(
                    onStartGame = {
                        // Start from first level
                        navigationState = NavigationState.Game(levelId = 1)
                    },
                    onLevelSelect = {
                        navigationState = NavigationState.LevelSelect
                    },
                    onLeaderboard = {
                        navigationState = NavigationState.Leaderboard
                    },
                    onAboutUs = {
                        // TODO: Implement about us
                    },
                    backgroundTheme = backgroundTheme,
                    onBackgroundThemeChange = { theme ->
                        backgroundTheme = theme
                        settingsStorage.saveBackgroundTheme(theme)
                    },
                    currentLanguage = currentLanguage,
                    onLanguageChange = { language ->
                        currentLanguage = language
                        settingsStorage.saveLanguage(language)
                    }
                )
            }
            is NavigationState.LevelSelect -> {
                LevelSelectScreen(
                    onBack = {
                        navigationState = NavigationState.Main
                    },
                    onLevelPackSelected = { levelPack ->
                        navigationState = NavigationState.Game(levelId = levelPack.id)
                    },
                    backgroundTheme = backgroundTheme
                )
            }
            is NavigationState.Game -> {
                GameScreen(
                    levelId = state.levelId,
                    onClose = {
                        navigationState = NavigationState.Main
                    },
                    backgroundTheme = backgroundTheme
                )
            }
            is NavigationState.Leaderboard -> {
                LeaderboardScreen(
                    onBack = {
                        navigationState = NavigationState.Main
                    },
                    backgroundTheme = backgroundTheme
                )
            }
        }
        }
    } // end ProvideStrings
}
